import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { FormControlLabel, Switch } from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1)
    }
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  hint: {
    opacity: 0.7,
    marginTop: theme.spacing(1)
  },
  imageRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  preview: {
    width: 72,
    height: 72,
    objectFit: "cover",
    borderRadius: 4,
    border: "1px solid rgba(0,0,0,0.12)"
  }
}));

const ProductSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("catalog.modal.validation.nameShort"))
    .max(191)
    .required(i18n.t("catalog.modal.validation.nameRequired")),
  price: Yup.string().required(i18n.t("catalog.modal.validation.priceRequired"))
});

const initialState = {
  name: "",
  description: "",
  price: "",
  currency: "BRL",
  retailerId: "",
  productUrl: "",
  isHidden: false
};

/** Centavos -> texto editável ("1990" -> "19,90"). */
const centsToInput = cents => {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) {
    return "";
  }
  return (Number(cents) / 100).toFixed(2).replace(".", ",");
};

/**
 * Texto -> centavos. Aceita "19,90", "19.90" e "1990,00"; ignora símbolo de
 * moeda e separador de milhar. O backend só trabalha com o inteiro.
 */
const inputToCents = value => {
  const cleaned = String(value || "").replace(/[^\d,.-]/g, "");
  if (!cleaned) return NaN;

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  const decimalSep = lastComma > lastDot ? "," : lastDot > lastComma ? "." : "";

  let normalized = cleaned;
  if (decimalSep) {
    const thousandSep = decimalSep === "," ? "." : ",";
    normalized = cleaned.split(thousandSep).join("").replace(decimalSep, ".");
  } else {
    normalized = cleaned.replace(/[.,]/g, "");
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : NaN;
};

const CatalogProductModal = ({
  open,
  onClose,
  reload,
  productId,
  whatsappId
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const [product, setProduct] = useState(initialState);
  const [imageUrl, setImageUrl] = useState("");
  // O Baileys prefere o caminho local (evita um HTTP para a própria máquina ao
  // subir a imagem); a Graph API precisa da URL pública. Guardamos os dois e o
  // backend usa o que servir para o canal.
  const [imagePath, setImagePath] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchProduct = async () => {
      if (!productId) {
        setProduct(initialState);
        setImageUrl("");
        setImagePath("");
        return;
      }
      try {
        const { data } = await api.get(`/catalog/products/${productId}`);
        setProduct({
          name: data.name || "",
          description: data.description || "",
          price: centsToInput(data.priceCents),
          currency: data.currency || "BRL",
          retailerId: data.retailerId || "",
          productUrl: data.productUrl || "",
          isHidden: Boolean(data.isHidden)
        });
        setImageUrl(data.imageUrl || "");
        setImagePath("");
      } catch (err) {
        toastError(err);
      }
    };

    fetchProduct();
  }, [productId, open]);

  const handleClose = () => {
    setProduct(initialState);
    setImageUrl("");
    setImagePath("");
    onClose();
  };

  const handleUpload = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const { data } = await api.post("/catalog/products/media-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setImageUrl(data.url);
      setImagePath(data.path);
    } catch (err) {
      toastError(err);
    }
    setUploading(false);
    // Permite reenviar o mesmo arquivo depois de um erro.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = async values => {
    const priceCents = inputToCents(values.price);
    if (Number.isNaN(priceCents)) {
      toast.error(i18n.t("catalog.modal.validation.priceInvalid"));
      return;
    }

    const payload = {
      ...values,
      priceCents,
      imageUrl,
      imagePath,
      whatsappId
    };
    delete payload.price;

    try {
      if (productId) {
        await api.put(`/catalog/products/${productId}`, payload);
      } else {
        await api.post("/catalog/products", payload);
      }
      toast.success(i18n.t("catalog.modal.saved"));
      if (reload) reload();
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>
          {productId
            ? i18n.t("catalog.modal.editTitle")
            : i18n.t("catalog.modal.addTitle")}
        </DialogTitle>
        <Formik
          initialValues={product}
          enableReinitialize
          validationSchema={ProductSchema}
          onSubmit={async (values, actions) => {
            await handleSave(values);
            actions.setSubmitting(false);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("catalog.modal.name")}
                  name="name"
                  autoFocus
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
                <Field
                  as={TextField}
                  label={i18n.t("catalog.modal.description")}
                  name="description"
                  multiline
                  minRows={3}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("catalog.modal.price")}
                    name="price"
                    placeholder="19,90"
                    error={touched.price && Boolean(errors.price)}
                    helperText={touched.price && errors.price}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("catalog.modal.currency")}
                    name="currency"
                    variant="outlined"
                    margin="dense"
                    style={{ width: 120 }}
                  />
                </div>
                <Field
                  as={TextField}
                  label={i18n.t("catalog.modal.retailerId")}
                  name="retailerId"
                  helperText={i18n.t("catalog.modal.retailerIdHint")}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
                <Field
                  as={TextField}
                  label={i18n.t("catalog.modal.productUrl")}
                  name="productUrl"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />

                <div className={classes.imageRow}>
                  {imageUrl && (
                    <img
                      className={classes.preview}
                      src={imageUrl}
                      alt={values.name}
                      onError={e => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={uploading}
                    startIcon={uploading ? <CircularProgress size={16} /> : null}
                  >
                    {imageUrl
                      ? i18n.t("catalog.modal.changeImage")
                      : i18n.t("catalog.modal.addImage")}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleUpload}
                    />
                  </Button>
                </div>

                <FormControlLabel
                  control={
                    <Switch
                      checked={values.isHidden}
                      onChange={e => setFieldValue("isHidden", e.target.checked)}
                      color="primary"
                    />
                  }
                  label={i18n.t("catalog.modal.isHidden")}
                />

                <Typography variant="body2" className={classes.hint}>
                  {i18n.t("catalog.modal.hint")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
                  {i18n.t("catalog.modal.cancel")}
                </Button>
                <div className={classes.btnWrapper}>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting || uploading}
                    variant="contained"
                  >
                    {i18n.t("catalog.modal.save")}
                  </Button>
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </div>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CatalogProductModal;
